import classnames from 'classnames'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatches } from 'react-router'
import { Link, Outlet, ScrollRestoration } from 'react-router-dom'
import LogoutIcon from '@material-symbols/svg-300/rounded/logout.svg'
import FavoriteIcon from '@material-symbols/svg-300/outlined/favorite.svg'

import ExpandedIncidator from '../basics/ExpandedIndicator/ExpandedIndicator'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import NotificationBell from '../NotificationBell/NotificationBell'
import SkillfitLogo from '../SkillfitLogo/SkillfitLogo'
import UserAvatar from '../UserAvatar/UserAvatar'
import Button from '../basics/Button/Button'
import Menu from '../basics/Menu/Menu'
import MainNav from '../MainNav/MainNav'
import Search from '../Search/Search'
import useInvalidate from '../../state/useInvalidate'
import { useBranding } from '../../branding'

import styles from './Page.module.scss'

export type FooterLinks = { title: string; links: { title: string; href: string }[] }[]
const DEFAULT_FOOTER_LINKS: FooterLinks = [
  {
    title: 'Unternehmen',
    links: [
      { title: 'Über Skillfit', href: 'https://www.skillfit.io/' },
      { title: 'Impressum', href: 'https://www.skillfit.io/impressum' },
    ],
  },
  {
    title: 'Support',
    links: [
      { title: 'Datenschutzerklärung', href: 'https://www.skillfit.io/datenschutzerklarung' },
      { title: 'Kontakt', href: 'mailto:support@skillfit.io' },
    ],
  },
]

export default React.memo(function Page() {
  const { t, i18n } = useTranslation()

  const invalidate = useInvalidate()

  const routes = useRoutes()

  const { user: currentUser, logout } = useSession()

  const sidebarExtensionRef = useRef<HTMLDivElement | null>(null)

  const [isFullscreen, setFullscreen] = useState(false)

  const pageContext = useMemo(
    () => ({
      sidebarExtension: sidebarExtensionRef,
      setFullscreen,
    }),
    [setFullscreen, sidebarExtensionRef.current]
  )

  const { footerLinks } = useBranding()

  // FIXME
  const matches = useMatches() as any
  const breadcrumbs = matches?.[matches.length - 1]?.handle?.breadcrumbs || []

  return (
    <div className={classnames(styles.Page, { [styles.fullscreen]: isFullscreen })}>
      <nav className={styles.breadcrumbs} aria-label={t('breadcrumbs')}>
        <ul>
          {breadcrumbs.map((B: any, idx: any) => (
            <li key={idx}>{B}</li>
          ))}
        </ul>
      </nav>
      <div className={styles.navigationElements}>
        <div className={styles.sidebar}>
          <MainNav />
          <div className={styles.sidebarExtension} ref={sidebarExtensionRef} />
        </div>
        <nav aria-hidden className={styles.userNav}>
          <Menu
            plain
            tabIndex={-1}
            label={currentUser && <UserAvatar omitRoleIcon className={styles.avatar} user={currentUser as any} />}
          >
            <li>
              {currentUser && (
                <Button menuItem component={Link} to={routes.manageUser.url({ uuid: currentUser.uuid })}>
                  {t('myProfile')}
                </Button>
              )}
            </li>
            <li>
              {currentUser && (
                <Button menuItem onClick={logout}>
                  <LogoutIcon fontSize={'inherit'} /> {t('login password.logout')}
                </Button>
              )}
            </li>
          </Menu>
        </nav>
        <div className={styles.searchBar}>
          <Search />
          <div className={styles.favorites}>
            <Button
              component={Link}
              onClick={invalidate.courses}
              small
              filled
              to={routes.courses.withQuery({ favorites: true }).url({})}
            >
              <FavoriteIcon />
            </Button>
          </div>
          <NotificationBell />
          <Menu
            label={
              <>
                <span>{i18n.language.toUpperCase()}</span>
                <ExpandedIncidator />
              </>
            }
            small
            plain
            className={styles.languageSwitch}
          >
            <li>
              <Button menuItem component={Link} to={routes.current.withLanguage('en').url({})}>
                English
              </Button>
            </li>
            <li>
              <Button menuItem component={Link} to={routes.current.withLanguage('de').url({})}>
                Deutsch
              </Button>
            </li>
          </Menu>
        </div>
      </div>
      <Outlet context={pageContext} />
      <footer className={styles.footer}>
        <SkillfitLogo />
        <nav>
          <ul>
            {(footerLinks || DEFAULT_FOOTER_LINKS).map(({ title, links }, i) => (
              <li key={i}>
                <span>{title}</span>
                <ul>
                  {(links || []).map(({ title, href }, i) => (
                    <li key={i}>
                      <a target={href.startsWith('http') ? '_blank' : undefined} href={href}>
                        {title}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
        <div className={styles.copyright}>© {new Date().getFullYear()} skillfit.io</div>
      </footer>
      <ScrollRestoration />
    </div>
  )
})
